/**
 * Context key under which the disposition engine reads the case's INPUT
 * `sites_of_care` array.
 *
 * The engine requires a site of care's `network_status` / `coverage_status` only when
 * that site carries an `npi` or a `tax_id` — a payer rep cannot look a facility up
 * without one, so a name-only site is unverifiable and must not block the disposition
 * (BV-2028). Both identifiers are nomos **Input-only** fields: `OutputSiteOfCare`
 * carries only `name`, `network_status`, `coverage_status`, `copay` and `coinsurance`.
 *
 * On the live call the engine's context IS the input array, so it reads them directly.
 * On this recompute path the `sites_of_care` it sees is the OUTPUT array — and a
 * name-only Output entry is indistinguishable from a name-only input site — so without
 * this key the engine fails open and keeps requiring a status for every site, which
 * would leave the dashboard's missing-field panel disagreeing with the case disposition
 * and the themis audit.
 *
 * Mirrors `SITES_OF_CARE_INPUT_KEY` in
 * `oai-realtime-with-twilio/packages/disposition-engine-js/src/workflows/unifiedmedbv_transform.ts`.
 * Duplicated here for the same reason as {@link INTERNAL_AGENT_ID_KEY} in `agentId.ts`:
 * `@100mslive/disposition-engine` does not export it from its entry point — replace
 * this constant with the package export once it does.
 */
export const SITES_OF_CARE_INPUT_KEY = '__sites_of_care_input__';

/**
 * The case's INPUT sites of care, when there are any. Anything else — a case with no
 * sites, a malformed envelope — yields `undefined` so the caller omits the key and the
 * engine keeps its fail-open behaviour, rather than gating on a value it cannot trust.
 */
export function inputSitesOfCare(
  inputData: Record<string, unknown> | null | undefined,
): unknown[] | undefined {
  const sites = inputData?.sites_of_care;
  return Array.isArray(sites) && sites.length > 0 ? sites : undefined;
}
